// Звонок 1:1 по v2; сервер с выключенным v2 переводит исходящий на legacy relay.
import { api, ApiError, randomUUID } from './api/client';
import { realtime } from './realtime';
import { applyIceServers, currentIceServers } from './rtcIce';
import { OUTGOING_RING_TIMEOUT_MS, rtcApp } from './rtc';
import { CallMachine, type CallSnapshot, type Out, type Phase, type V2Event } from './rtcV2Machine';

export { RTC_V2_FLAG_KEY, rtcV2Enabled, setRtcV2Enabled } from './rtcProtocol';
import { isDirectV2Disabled, rememberDirectV2Disabled } from './rtcProtocol';
/** Период heartbeat: lease сессии на сервере 30 с, hb каждые 10 с (calls.md §7). */
export const HEARTBEAT_MS = 10_000;
const ICE_RESTART_DELAY_MS = 6000;

/** Событие протокола v2 — по полю `v` (v1-сигналы его не содержат). */
export function isV2Event(raw: unknown): raw is V2Event {
  return !!raw && typeof raw === 'object' && (raw as { v?: unknown }).v === 2 && typeof (raw as { type?: unknown }).type === 'string';
}

/** Ответ «занят» на входящий звонок, когда пользователь уже в другом звонке. */
export function replyUnavailable(chatId: string, callId: string): void {
  realtime.publish(rtcApp(chatId), CallMachine.busyReply(callId));
}

/** Снимок звонка по REST; null — 404 (неизвестен / протокол выключен / tombstone истёк). */
export async function fetchCallSnapshot(chatId: string, callId?: string | null): Promise<CallSnapshot | null> {
  const path = callId ? `/api/v1/chats/${chatId}/calls/${callId}` : `/api/v1/chats/${chatId}/call`;
  try {
    return await api<CallSnapshot>(path);
  } catch (e) {
    if (e instanceof ApiError && e.status === 404) return null;
    throw e;
  }
}

export interface RtcV2Handlers {
  onRemoteStream: (stream: MediaStream) => void;
  onPhase: (phase: Phase, m: CallMachine) => void;
  onFinished: (info: Extract<Out, { k: 'finished' }>) => void;
  onRejected?: (reason: string) => void;
  onLegacyFallback?: (stream: MediaStream | undefined, video: boolean, deadlineAt: number) => void;
  onGlare?: () => void;
  onRemoteMuteState?: (kind: 'audio' | 'video', muted: boolean) => void;
  onRemoteHold?: (held: boolean) => void;
}

export class RtcV2Session {
  readonly machine: CallMachine;
  readonly chatId: string;
  readonly pc: RTCPeerConnection;
  localStream?: MediaStream;
  private handlers: RtcV2Handlers;
  private destroyed = false;
  private remoteDescSet = false;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private facingMode: 'user' | 'environment' = 'user';
  private switchingCamera = false;
  private localHeld = false;
  private micMuted = false;
  private cameraMuted = false;
  private outgoingDeadlineAt = 0;
  private hbTimer?: ReturnType<typeof setInterval>;
  private disconnectTimer?: ReturnType<typeof setTimeout>;

  constructor(
    chatId: string,
    meId: string,
    handlers: RtcV2Handlers,
    publish: (dest: string, body: unknown) => boolean = (d, b) => realtime.publish(d, b),
  ) {
    this.chatId = chatId;
    this.handlers = handlers;
    this.machine = new CallMachine(chatId, meId, (o) => this.onOut(o, publish), randomUUID);
    this.pc = new RTCPeerConnection({ iceServers: currentIceServers() });
    this.pc.onicecandidate = (e) => this.machine.sendIce(e.candidate ? e.candidate.toJSON() : null);
    this.pc.ontrack = (e) => this.handlers.onRemoteStream(e.streams[0]);
    this.pc.onconnectionstatechange = () => {
      const state = this.pc.connectionState;
      if (state === 'connected') {
        this.clearDisconnectTimer();
        this.machine.mediaConnected();
      } else if (state === 'disconnected') {
        this.clearDisconnectTimer();
        this.disconnectTimer = setTimeout(() => {
          if (this.destroyed || this.pc.connectionState !== 'disconnected') return;
          try { this.pc.restartIce(); } catch { /* закрыт */ }
        }, ICE_RESTART_DELAY_MS);
      } else if (state === 'failed') {
        this.clearDisconnectTimer();
        this.machine.hangup('ice_failed');
      }
    };
  }

  // ------------------------------------------------------------------ управление

  async startWithMedia(audio: boolean, video: boolean): Promise<void> {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio,
      video: video ? { facingMode: this.facingMode } : false,
    });
    await this.startWithStream(stream);
  }

  async startWithStream(stream: MediaStream, audioEnabled = true, videoEnabled = true): Promise<void> {
    if (this.destroyed) {
      stream.getTracks().forEach((track) => track.stop());
      return;
    }
    this.localStream = stream;
    this.micMuted = !audioEnabled;
    this.cameraMuted = !videoEnabled;
    stream.getAudioTracks().forEach((track) => { track.enabled = audioEnabled && !this.localHeld; });
    stream.getVideoTracks().forEach((track) => { track.enabled = videoEnabled && !this.localHeld; });
    try {
      stream.getTracks().forEach((track) => this.pc.addTrack(track, stream));
    } catch (error) {
      stream.getTracks().forEach((track) => track.stop());
      this.localStream = undefined;
      throw error;
    }
  }

  get isDestroyed(): boolean {
    return this.destroyed;
  }

  /** Создать звонок (медиа уже получено; SDP на этом шаге нет — он создаётся после `accepted`). */
  startOutgoing(video: boolean): void {
    this.outgoingDeadlineAt = Date.now() + OUTGOING_RING_TIMEOUT_MS;
    this.machine.startOutgoing(video);
  }

  receiveIncoming(callId: string, video: boolean, deadlineAt = 0): void {
    this.machine.receiveIncoming(callId, video, deadlineAt);
  }

  accept(): void {
    this.machine.accept();
  }

  announceInitialMuteState(): void {
    if (this.micMuted) this.machine.sendMute('audio', true);
    if (this.cameraMuted) this.machine.sendMute('video', true);
  }

  reject(): void {
    this.machine.decline();
  }

  hangup(): void {
    this.machine.hangup();
  }

  setHeld(held: boolean): void {
    if (this.destroyed || this.localHeld === held) return;
    this.localHeld = held;
    this.localStream?.getAudioTracks().forEach((track) => { track.enabled = !held && !this.micMuted; });
    this.localStream?.getVideoTracks().forEach((track) => { track.enabled = !held && !this.cameraMuted; });
    this.machine.setHeld(held);
  }

  announceHoldState(): void {
    if (!this.destroyed) this.machine.announceHoldState();
  }

  get isHeld(): boolean { return this.localHeld; }

  onEvent(ev: V2Event): void {
    if (this.destroyed) return;
    if (this.machine.phase === 'creating' && (!ev.chatId || ev.chatId === this.chatId) && isDirectV2Disabled(ev)) {
      rememberDirectV2Disabled();
      if (!this.machine.isHangupPending && this.handlers.onLegacyFallback) {
        const stream = this.localStream;
        this.localStream = undefined; // передаём дорожки v1 без повторного запроса разрешений
        this.destroy();
        this.handlers.onLegacyFallback(stream, this.machine.video, this.outgoingDeadlineAt);
        return;
      }
    }
    this.machine.onEvent(ev);
  }

  /** Сверка с сервером при возврате в приложение / переподключении WS. */
  async refreshSnapshot(): Promise<void> {
    if (this.destroyed || this.machine.isFinished) return;
    const id = this.machine.callId;
    if (!id) return;
    let snap: CallSnapshot | null;
    try {
      snap = await fetchCallSnapshot(this.chatId, id);
    } catch {
      return; // сеть недоступна — решит lease/heartbeat на сервере
    }
    if (this.destroyed) return;
    this.machine.applySnapshot(snap);
  }

  toggleMic(): boolean {
    const track = this.localStream?.getAudioTracks()[0];
    if (!track) return false;
    this.micMuted = !this.micMuted;
    track.enabled = !this.micMuted && !this.localHeld;
    this.machine.sendMute('audio', this.micMuted);
    return this.micMuted;
  }

  toggleCamera(): boolean {
    const track = this.localStream?.getVideoTracks()[0];
    if (!track) return false;
    this.cameraMuted = !this.cameraMuted;
    track.enabled = !this.cameraMuted && !this.localHeld;
    this.machine.sendMute('video', this.cameraMuted);
    return this.cameraMuted;
  }

  async switchCamera(): Promise<void> {
    if (!this.localStream || this.switchingCamera) return;
    const oldTrack = this.localStream.getVideoTracks()[0];
    if (!oldTrack) return;
    this.switchingCamera = true;
    let newTrack: MediaStreamTrack | undefined;
    try {
      this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
      const newStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: this.facingMode } });
      newTrack = newStream.getVideoTracks()[0];
      newTrack.enabled = !this.cameraMuted && !this.localHeld;
      const sender = this.pc.getSenders().find((s) => s.track?.kind === 'video');
      if (sender) await sender.replaceTrack(newTrack);
      oldTrack.stop();
      this.localStream.removeTrack(oldTrack);
      this.localStream.addTrack(newTrack);
    } catch {
      newTrack?.stop();
      this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
    } finally {
      this.switchingCamera = false;
    }
  }

  /** Глэр: наш исходящий не состоялся — освободить камеру/микрофон до явного «Принять». */
  releaseLocalMedia(): void {
    this.localStream?.getTracks().forEach((t) => t.stop());
    for (const sender of this.pc.getSenders()) {
      try { this.pc.removeTrack(sender); } catch { /* закрыт */ }
    }
    this.localStream = undefined;
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.clearDisconnectTimer();
    this.stopHeartbeat();
    this.localStream?.getTracks().forEach((t) => t.stop());
    try { this.pc.close(); } catch { /* уже закрыт */ }
  }

  // -------------------------------------------------------------------- внутреннее

  private onOut(o: Out, publish: (dest: string, body: unknown) => boolean): void {
    switch (o.k) {
      case 'send':
        // WS не подключён — `call` потерян, ждать `created` бессмысленно
        if (!publish(rtcApp(this.chatId), { ...o.body, v: 2 }) && o.body.type === 'call') {
          queueMicrotask(() => this.machine.abort('send_failed'));
        }
        break;
      case 'phase':
        this.handlers.onPhase(o.phase, this.machine);
        break;
      case 'hb':
        if (o.on) this.startHeartbeat();
        else this.stopHeartbeat();
        break;
      case 'needOffer':
        void this.makeOffer();
        break;
      case 'remoteOffer':
        void this.applyRemoteOffer(o.negId, o.sdp);
        break;
      case 'remoteAnswer':
        void this.applyRemoteAnswer(o.sdp);
        break;
      case 'remoteIce':
        void this.applyRemoteIce(o.candidate);
        break;
      case 'remoteMute':
        this.handlers.onRemoteMuteState?.(o.kind, o.muted);
        break;
      case 'remoteHold':
        this.handlers.onRemoteHold?.(o.held);
        break;
      case 'glare':
        this.releaseLocalMedia();
        this.handlers.onGlare?.();
        break;
      case 'rejected':
        this.handlers.onRejected?.(o.reason);
        break;
      case 'finished':
        this.handlers.onFinished(o);
        break;
    }
  }

  private startHeartbeat(): void {
    if (this.hbTimer != null) return;
    this.hbTimer = setInterval(() => this.machine.heartbeat(), HEARTBEAT_MS);
  }

  private stopHeartbeat(): void {
    if (this.hbTimer != null) {
      clearInterval(this.hbTimer);
      this.hbTimer = undefined;
    }
  }

  private clearDisconnectTimer(): void {
    if (this.disconnectTimer != null) {
      clearTimeout(this.disconnectTimer);
      this.disconnectTimer = undefined;
    }
  }

  private fail(e: unknown): void {
    console.warn('[rtc-v2] negotiation error', e);
    this.machine.hangup('error');
  }

  private async makeOffer(): Promise<void> {
    try {
      await applyIceServers(this.pc);
      const offer = await this.pc.createOffer();
      // offer уходит ДО setLocalDescription: negId нужен, чтобы кандидаты (которые пойдут
      // сразу после setLocalDescription) были привязаны к этим переговорам.
      this.machine.sendOffer(offer);
      await this.pc.setLocalDescription(offer);
    } catch (e) {
      this.fail(e);
    }
  }

  private async applyRemoteOffer(negId: number, sdp: RTCSessionDescriptionInit): Promise<void> {
    try {
      await applyIceServers(this.pc);
      await this.pc.setRemoteDescription(sdp);
      await this.flushCandidates();
      const answer = await this.pc.createAnswer();
      this.machine.sendAnswer(negId, answer);
      await this.pc.setLocalDescription(answer);
    } catch (e) {
      this.fail(e);
    }
  }

  private async applyRemoteAnswer(sdp: RTCSessionDescriptionInit): Promise<void> {
    try {
      await this.pc.setRemoteDescription(sdp);
      await this.flushCandidates();
    } catch (e) {
      this.fail(e);
    }
  }

  private async flushCandidates(): Promise<void> {
    this.remoteDescSet = true;
    const list = this.pendingCandidates;
    this.pendingCandidates = [];
    for (const c of list) {
      try { await this.pc.addIceCandidate(c); } catch { /* битый кандидат */ }
    }
  }

  private async applyRemoteIce(candidate: RTCIceCandidateInit | null): Promise<void> {
    if (!candidate) return; // конец сбора
    if (!this.remoteDescSet) {
      this.pendingCandidates.push(candidate);
      return;
    }
    try { await this.pc.addIceCandidate(candidate); } catch { /* транзитная ошибка не рушит звонок */ }
  }
}
